/**
 * Activation report library, section catalogue and deep-dive modules.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * Section completeness is NOT stored per report. `src/domain/activation`
 * derives it from the manual/auto input counts below plus whatever the builder
 * has recorded in `filled`. The 2.0 build hardcoded a percentage per section,
 * so editing a section never moved the number.
 */

import type {
  ActivationDataset,
  ActivationDeepDive,
  ActivationManualInput,
  ActivationReport,
  ActivationSection,
  ActivationVocabulary,
} from '../../contracts';

export const AR_SECTIONS: readonly ActivationSection[] = [
  { id: 'cover', name: 'Cover', required: true, manual: 2, auto: 3 },
  { id: 'welcome', name: 'Welcome / Letter', required: true, manual: 1, auto: 1 },
  { id: 'contents', name: 'Contents', required: true, manual: 0, auto: 1 },
  { id: 'exec-summary', name: 'Executive Summary', required: true, manual: 5, auto: 3 },
  { id: 'media', name: 'Media', required: false, manual: 2, auto: 6 },
  { id: 'brand-impact', name: 'Brand Impact', required: false, manual: 4, auto: 0 },
  { id: 'stadium', name: 'Stadium & Hospitality', required: false, manual: 1, auto: 4 },
  { id: 'activations', name: 'Activations', required: false, manual: 8, auto: 5 },
  { id: 'outlook', name: 'Outlook / Next Season', required: false, manual: 6, auto: 2 },
  { id: 'methodology', name: 'Methodology', required: false, manual: 2, auto: 3 },
];

export const AR_DEEPDIVES: readonly ActivationDeepDive[] = [
  {
    id: 'media-overview',
    name: 'Media Value Overview',
    cat: 'Media',
    desc: 'Multi-channel total value with breakdown chart',
    auto: ['Total value', 'Channel breakdown', 'Period comparison'],
    manual: ['Key insights', 'Source notes'],
    available: true,
  },
  {
    id: 'tv-exposure',
    name: 'TV Exposure Deep Dive',
    cat: 'Media',
    desc: 'Exposure time, reach, media value and key broadcast moments',
    auto: ['Exposure seconds', 'Reach', 'Media value', 'Broadcast schedule'],
    manual: ['Key moments description', 'Frame selection'],
    available: true,
  },
  {
    id: 'social-media',
    name: 'Social Media Exposure',
    cat: 'Media',
    desc: 'Growth, posts, impressions and engagement by platform',
    auto: ['Follower growth', 'Post count', 'Impressions', 'Engagement rate'],
    manual: ['Best post highlights', 'Key insights'],
    available: true,
  },
  {
    id: 'kit-launch',
    name: 'Kit Launch Deep Dive',
    cat: 'Activations',
    desc: 'Kit reveal campaign with selected posts and metrics',
    auto: ['Launch date', 'Social metrics'],
    manual: ['Post selection', 'Campaign narrative', 'Learnings'],
    available: true,
  },
  {
    id: 'awareness',
    name: 'Sponsorship Awareness',
    cat: 'Brand Impact',
    desc: 'Recognition, recall and competitor benchmarking',
    auto: [],
    manual: ['Survey data', 'Charts', 'Insights'],
    available: false,
  },
  {
    id: 'stadium-attendance',
    name: 'Stadium Attendance & Hospitality',
    cat: 'Stadium',
    desc: 'Utilisation by game, experience scores and audience profile',
    auto: ['Attendance by game', 'Utilisation %', 'Guest count'],
    manual: ['Guest feedback quotes', 'Experience highlights'],
    available: true,
  },
];

export const AR_DATASETS: readonly ActivationDataset[] = [
  {
    name: 'Media Value & Breakdown',
    status: 'Available',
    source: 'System',
    desc: 'Total media value with TV, digital, print and social breakdown',
  },
  {
    name: 'Social Media Exposure',
    status: 'Available',
    source: 'System',
    desc: 'Followers, posts, impressions and engagement metrics',
  },
  {
    name: 'Activations List',
    status: 'Partial',
    source: 'System',
    desc: 'Planned and executed activations with images (15 of 23 have images)',
  },
  {
    name: 'Stadium & Hospitality',
    status: 'Available',
    source: 'System',
    desc: 'Attendance, utilisation and guest experience data',
  },
  {
    name: 'Brand Impact Survey',
    status: 'Missing',
    source: 'Manual',
    desc: 'Brand awareness, consideration and perception metrics',
  },
];

export const AR_VOCABULARY: ActivationVocabulary = {
  rightHolders: [
    ['real-madrid', 'Real Madrid CF'],
    ['arsenal', 'Arsenal FC'],
    ['bayern', 'FC Bayern Munich'],
    ['manchester-united', 'Manchester United'],
    ['uefa-cl', 'UEFA Champions League'],
    ['sk-rapid', 'SK Rapid Wien'],
  ],
  partners: [
    ['adidas', 'Adidas'],
    ['emirates', 'Emirates'],
    ['allianz', 'Allianz'],
    ['coca-cola', 'Coca-Cola'],
    ['nike', 'Nike'],
    ['wien-energie', 'Wien Energie'],
  ],
};

export const AR_REPORT_TYPES: readonly string[] = [
  'End of Season',
  'Mid-Season',
  'Activation Summary',
  'Custom',
];

export const AR_LANGUAGES: readonly string[] = ['English', 'Spanish', 'German', 'French'];

export const AR_MANUAL_INPUTS: readonly ActivationManualInput[] = [
  {
    id: 'mi-1',
    type: 'quote',
    content:
      'The partnership delivered exceptional value, particularly during the Champions League campaign where we saw record engagement.',
    author: 'John Smith',
    title: 'Partnership Director',
    visibility: 'partner',
    owner: 'Sarah Johnson',
    lastEdited: '2026-03-15',
    tags: ['Media', 'Highlight'],
  },
  {
    id: 'mi-2',
    type: 'insight',
    content:
      'Social media engagement increased 47% year on year, driven primarily by kit launch content and matchday activations.',
    visibility: 'partner',
    owner: 'Mike Chen',
    lastEdited: '2026-03-14',
    tags: ['Social Media', 'Performance'],
  },
  {
    id: 'mi-3',
    type: 'recommendation',
    content:
      'Expand digital-first activations in Q1 2025/26 to capitalise on strong social performance trends.',
    visibility: 'internal',
    owner: 'Sarah Johnson',
    lastEdited: '2026-03-13',
    tags: ['Strategy', 'Next Steps'],
  },
];

export const SEED_ACTIVATION: readonly ActivationReport[] = [
  {
    id: 'ar-1',
    type: 'End of Season',
    title: 'End of Season Report 2024/25',
    partner: 'Adidas',
    rightHolder: 'Real Madrid CF',
    season: '2024/2025',
    lastModified: '2026-03-15',
    status: 'Draft',
    sections: [
      'cover',
      'welcome',
      'contents',
      'exec-summary',
      'media',
      'stadium',
      'activations',
      'outlook',
      'methodology',
    ],
    deepDives: ['media-overview', 'tv-exposure', 'social-media'],
    filled: {},
  },
  {
    id: 'ar-2',
    type: 'Mid-Season',
    title: 'Mid-Season Review',
    partner: 'Emirates',
    rightHolder: 'Arsenal FC',
    season: '2024/2025',
    lastModified: '2026-03-10',
    status: 'Approved',
    sections: [
      'cover',
      'welcome',
      'contents',
      'exec-summary',
      'media',
      'activations',
      'methodology',
    ],
    deepDives: ['media-overview', 'social-media'],
    complete: true,
  },
  {
    id: 'ar-3',
    type: 'End of Season',
    title: 'End of Season Report 2023/24',
    partner: 'Allianz',
    rightHolder: 'FC Bayern Munich',
    season: '2023/2024',
    lastModified: '2026-02-28',
    status: 'Shared',
    sections: [
      'cover',
      'welcome',
      'contents',
      'exec-summary',
      'media',
      'brand-impact',
      'stadium',
      'activations',
      'outlook',
      'methodology',
    ],
    deepDives: ['media-overview', 'tv-exposure', 'social-media', 'stadium-attendance'],
    complete: true,
  },
  {
    id: 'ar-4',
    type: 'Activation Summary',
    title: 'Q1 Activation Summary',
    partner: 'Coca-Cola',
    rightHolder: 'UEFA Champions League',
    season: '2024/2025',
    lastModified: '2026-03-05',
    status: 'In Review',
    sections: ['cover', 'contents', 'exec-summary', 'activations', 'methodology'],
    deepDives: ['kit-launch'],
    filled: { 'exec-summary': 0.8, activations: 0.9 },
  },
];

export const AR_TEMPLATES: readonly (readonly [string, string, string])[] = [
  [
    'End of Season',
    'trophy',
    'Comprehensive season wrap-up with media, brand impact and activations',
  ],
  ['Mid-Season', 'barChart', 'Progress review at the season midpoint with outlook and adjustments'],
  [
    'Activation Summary',
    'target',
    'Focused report on specific activation campaigns and their impact',
  ],
  ['Custom', 'sparkles', 'Build a custom report from scratch with flexible sections'],
];
